<template>
  <v-snackbar
    v-bind="snackbarProps"
    :content-class="!!snackbarProps.custom ? 'custom-toast' : ''"
    @update:model-value="onSnackbarInput"
  >
    <component
      :is="activeToast.component"
      v-if="activeToast?.component"
      v-bind="resolvedComponentProps"
      @close="() => onSnackbarInput(false)"
    />
    <span v-else v-text="activeToast?.text" />
  </v-snackbar>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, defineProps } from 'vue'
import { sleep } from '@/utils/helpers'
import bus from '@/plugins/eventbus'

const props = defineProps({
  replaceActive: {
    type: Boolean,
    default: true,
  },
})

const toasts = ref([])
const show = ref(false)

const activeToast = computed(() => toasts.value[0])

const resolvedComponentProps = computed(() => {
  const toast = activeToast.value
  if (!toast) return {}
  
  if (typeof toast.componentProps === 'function') {
    return toast.componentProps()
  }

  return toast.componentProps || {}
})

const snackbarProps = computed(() => {
  const opts = {
    app: true,
    modelValue: show.value,
    transition: 'slide-y-reverse-transition',
    color: 'mint25 text-mint600',
  }

  const toast = activeToast.value
  if (!toast) return opts

  return {
    ...opts,
    ...toast.props,
  }
})

watch(show, val => {
  if (val) {
    onSnackbarOpen(activeToast.value)
  }
})

onMounted(() => {
  bus.on('toast:open', onOpenToast)
  bus.on('toast:close-all', () => {
    show.value = false
    toasts.value = []
  })
})

function onSnackbarOpen() {
  // NOTE play sound?
}

async function onOpenToast(opts) {
  if (props.replaceActive && show.value) {
    await onSnackbarInput(false)
  }

  const newToast = {
    ...opts,
    props: {
      timeout: 2e3,
      ...opts.props,
    },
  }

  toasts.value.push(newToast)

  if (!show.value) {
    show.value = true
  }
}

async function onSnackbarInput(open) {
  if (open) return

  // Call the toast's close callbacks before removing it
  // This allows components like ImportToastManager to sync their state
  const closingToast = toasts.value[0]
  if (closingToast) {
    // Support onClose callback
    if (typeof closingToast.onClose === 'function') {
      try {
        closingToast.onClose()
      } catch (error) {
        console.error('[ToastFactory] Error calling onClose:', error)
      }
    }
    // Support listeners.close for backward compatibility
    if (typeof closingToast.listeners?.close === 'function') {
      try {
        closingToast.listeners.close()
      } catch (error) {
        console.error('[ToastFactory] Error calling listeners.close:', error)
      }
    }
  }

  show.value = false

  await nextTick()
  await sleep(100)
  toasts.value.shift()

  if (toasts.value.length) {
    await nextTick()
    show.value = true
  }
}
</script>

<style>
.custom-toast {
  padding: 0 !important;
  .v-snackbar__content {
    padding: 0 !important;
  }
}
</style>
